/**
 * FinancialUpload.js
 * Upload tab — drag-and-drop bank statement upload with preview.
 * Calls /fin-upload/inspect (preview) and /fin-upload (commit) using API_BASE.
 */
import React, { useState, useRef, useEffect } from "react";
import api from "./apiClient";
import { useTheme, getT, glassCard } from "./ThemeContext";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const IcoUpload = () => (
  <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    <path d="M12 4v11m0-11L8.5 7.5M12 4l3.5 3.5" />
  </svg>
);

const IcoFile = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

function Spinner({ T, size = 36 }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      border: "2px solid " + T.divider,
      borderTopColor: T.yellow,
      animation: "fin-spin 0.7s linear infinite",
    }}>
      <style>{`@keyframes fin-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

export default function FinancialUpload({ onComplete }) {
  const { isDark } = useTheme();
  const T  = getT(isDark);
  const gc = glassCard(T);
  const { fmtDateTime } = useDateTimeFormat();

  const [dragging,    setDragging]    = useState(false);
  const [stage,       setStage]       = useState("idle");     // idle|inspecting|preview|uploading|done
  const [preview,     setPreview]     = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [status,      setStatus]      = useState(null);
  const [history,     setHistory]     = useState([]);
  const inputRef = useRef();

  const sampleRowsSort = useSort(preview?.sample_rows || [], "date");
  const historySort = useSort(history, "upload_date", "desc");

  useEffect(() => {
    api.get("/fin-upload/history")
      .then(r => setHistory(r.data))
      .catch(() => {});
  }, []);

  const runInspect = async (file) => {
    setStatus(null); setPreview(null); setPendingFile(file); setStage("inspecting");
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/fin-upload/inspect", form);
      setPreview(res.data);
      setStage("preview");
    } catch (err) {
      setStatus({ type: "error", msg: "Inspect failed: " + (err.response?.data?.detail || err.message) });
      setStage("idle");
    }
  };

  const runUpload = async () => {
    if (!pendingFile) return;
    setStage("uploading"); setStatus(null);
    const form = new FormData();
    form.append("file", pendingFile);
    try {
      const res = await api.post("/fin-upload", form);
      setStatus({ type: "success", msg: "Uploaded — " + res.data.record_count + " records processed." });
      setStage("done");
      api.get("/fin-upload/history").then(r => setHistory(r.data)).catch(() => {});
      setTimeout(() => onComplete?.(), 1500);
    } catch (err) {
      setStatus({ type: "error", msg: "Upload failed: " + (err.response?.data?.detail || err.message) });
      setStage("preview");
    }
  };

  const reset = () => { setStage("idle"); setPreview(null); setPendingFile(null); setStatus(null); };

  const thStyle = {
    padding: "11px 20px", fontWeight: 800,
    fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.10em",
    color: T.textMuted, borderBottom: "1px solid " + T.divider,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

      {/* Title */}
      <div>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: T.textPrimary }}>
          Upload Bank Statement
        </h2>
        <p style={{ margin: "5px 0 0", fontSize: 12, color: T.textMuted }}>
          Supports Bank of America and Chase CSV / Excel files. A preview is shown before data is committed.
        </p>
      </div>

      {/* Drop zone */}
      {(stage === "idle" || stage === "done") && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) runInspect(f); }}
          onClick={() => inputRef.current.click()}
          style={{
            ...gc,
            padding: 52, textAlign: "center", cursor: "pointer",
            border: dragging ? "2px dashed " + T.yellow : "2px dashed " + T.inputBdr,
            background: dragging ? (T.isDark ? "rgba(247,206,11,0.07)" : "rgba(247,206,11,0.05)") : T.glass,
            transition: "border-color 0.18s, background 0.18s",
          }}
        >
          <div style={{ color: T.textMuted, display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <IcoUpload />
          </div>
          <p style={{ margin: 0, color: T.textSub, fontWeight: 600, fontSize: 14 }}>
            Drag &amp; drop your bank statement here
          </p>
          <p style={{ margin: "5px 0 0", color: T.textMuted, fontSize: 12 }}>
            or click to browse · .csv .xlsx .xls .tsv .txt
          </p>
          <input
            ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.tsv,.txt"
            style={{ display: "none" }}
            onChange={e => { const f = e.target.files[0]; if (f) runInspect(f); e.target.value = ""; }}
          />
        </div>
      )}

      {/* Spinner states */}
      {(stage === "inspecting" || stage === "uploading") && (
        <div style={{
          ...gc, padding: 44, display: "flex", flexDirection: "column",
          alignItems: "center", gap: 14, color: T.textSub, fontSize: 13,
        }}>
          <Spinner T={T} />
          {stage === "inspecting" ? "Inspecting file…" : "Uploading and categorizing…"}
        </div>
      )}

      {/* Preview */}
      {stage === "preview" && preview && (
        <div style={{ ...gc, overflow: "hidden" }}>
          {/* Preview header */}
          <div style={{
            padding: "14px 20px", display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            borderBottom: "1px solid " + T.divider, background: T.glass,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ color: T.yellow }}><IcoFile /></div>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: T.textPrimary, fontSize: 13 }}>
                  {preview.filename}
                </p>
                <p style={{ margin: "3px 0 0", fontSize: 11, color: T.textMuted }}>
                  Bank: <span style={{ color: T.accentText, fontWeight: 700 }}>{preview.bank_detected}</span>
                  {" · "}Separator: {preview.separator}
                  {" · "}Header row {preview.header_row_index}
                  {" · "}<span style={{ color: T.accentText, fontWeight: 700 }}>{preview.total_rows_parsed} rows</span>
                  {preview.rows_skipped > 0 && <>{" · "}<span style={{ color: T.orange }}>{preview.rows_skipped} skipped</span></>}
                </p>
              </div>
            </div>
            <button onClick={reset} style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.textMuted, fontSize: 18, lineHeight: 1, padding: "0 0 0 14px",
            }}>✕</button>
          </div>

          {/* Warnings */}
          {preview.warnings?.length > 0 && (
            <div style={{
              padding: "8px 20px", fontSize: 11, color: T.orange,
              background: T.orangeBg, borderBottom: "1px solid " + T.orange + "33",
            }}>
              {preview.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}

          {/* Columns detected */}
          <div style={{ padding: "12px 20px", borderBottom: "1px solid " + T.divider }}>
            <p style={{
              margin: "0 0 8px", fontSize: 9.5, fontWeight: 800, color: T.textMuted,
              textTransform: "uppercase", letterSpacing: "0.12em",
            }}>Columns detected</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {preview.columns_found.map(c => (
                <span key={c} style={{
                  fontSize: 11, padding: "2px 10px", borderRadius: 999,
                  color: T.accentText,
                  background: T.isDark ? "rgba(247,206,11,0.10)" : "rgba(247,206,11,0.14)",
                  border: "1px solid " + T.yellow + "33",
                }}>{c}</span>
              ))}
            </div>
          </div>

          {/* Sample rows */}
          {preview.sample_rows?.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <p style={{
                padding: "14px 20px 7px", margin: 0,
                fontSize: 9.5, fontWeight: 800, color: T.textMuted,
                textTransform: "uppercase", letterSpacing: "0.12em",
              }}>
                First {preview.sample_rows.length} rows
              </p>
              <table style={{ minWidth: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid " + T.divider }}>
                    {[
                      { label: "Date", key: "date" },
                      { label: "Description", key: "description" },
                      { label: "Amount", key: "amount" },
                      { label: "Balance", key: "balance" },
                    ].map(({ label: h, key }) => (
                      <th key={h}
                        onClick={() => sampleRowsSort.toggleSort(key)}
                        style={{
                          ...thStyle,
                          textAlign: (h === "Amount" || h === "Balance") ? "right" : "left",
                          cursor: "pointer", userSelect: "none",
                          color: sampleRowsSort.sortKey === key ? T.yellow : thStyle.color,
                        }}>{h}{sampleRowsSort.sortArrow(key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sampleRowsSort.sorted.map((r, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid " + T.divider }}>
                      <td style={{ padding: "8px 20px", whiteSpace: "nowrap", color: T.textMuted }}>{r.date}</td>
                      <td style={{
                        padding: "8px 20px", color: T.textPrimary,
                        maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }} title={r.description}>{r.description}</td>
                      <td style={{
                        padding: "8px 20px", textAlign: "right", fontWeight: 700,
                        color: Number(r.amount) >= 0 ? T.green : T.textSub,
                        fontVariantNumeric: "tabular-nums",
                      }}>{r.amount}</td>
                      <td style={{
                        padding: "8px 20px", textAlign: "right", color: T.textMuted,
                        fontVariantNumeric: "tabular-nums",
                      }}>{r.balance || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Actions */}
          <div style={{
            padding: "14px 20px", display: "flex", gap: 10,
            borderTop: "1px solid " + T.divider, background: T.glass,
          }}>
            <button onClick={runUpload} style={{
              fontSize: 13, fontWeight: 700, padding: "8px 20px", borderRadius: 10,
              background: T.isDark ? "rgba(247,206,11,0.18)" : "rgba(247,206,11,0.22)",
              border: "1px solid " + T.yellow + "55",
              color: T.accentText, cursor: "pointer", fontFamily: "inherit",
            }}>
              Confirm &amp; Upload {preview.total_rows_parsed} rows
            </button>
            <button onClick={reset} style={{
              fontSize: 13, color: T.textMuted, background: "none", border: "none",
              cursor: "pointer", padding: "8px 12px", fontFamily: "inherit",
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Status message */}
      {status && (
        <div style={{
          padding: "10px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          color:      status.type === "success" ? T.green  : T.red,
          background: status.type === "success" ? T.greenBg : T.redBg,
          border: "1px solid " + (status.type === "success" ? T.green : T.red) + "44",
        }}>
          {status.msg}
        </div>
      )}

      {/* Upload history */}
      {history.length > 0 && (
        <div>
          <h3 style={{
            margin: "0 0 10px", fontSize: 10, fontWeight: 800, color: T.textMuted,
            textTransform: "uppercase", letterSpacing: "0.12em",
          }}>Upload History</h3>
          <div style={{ ...gc, borderRadius: 14, overflowX: "auto" }}>
            <table style={{ minWidth: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid " + T.divider }}>
                  {[
                    { label: "File", key: "original_name" },
                    { label: "Date", key: "upload_date" },
                    { label: "Records", key: "record_count" },
                    { label: "Status", key: "status" },
                  ].map(({ label: h, key }) => (
                    <th key={h}
                      onClick={() => historySort.toggleSort(key)}
                      style={{
                        ...thStyle,
                        textAlign: h === "Records" ? "right" : "left",
                        cursor: "pointer", userSelect: "none",
                        color: historySort.sortKey === key ? T.yellow : thStyle.color,
                      }}>{h}{historySort.sortArrow(key)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historySort.sorted.map(h => (
                  <tr key={h.id}
                    style={{ borderBottom: "1px solid " + T.divider, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.glass}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "9px 20px", fontWeight: 600, color: T.textPrimary }}>{h.original_name}</td>
                    <td style={{ padding: "9px 20px", color: T.textMuted }}>{fmtDateTime(h.upload_date)}</td>
                    <td style={{ padding: "9px 20px", textAlign: "right", color: T.textSub, fontVariantNumeric: "tabular-nums" }}>{h.record_count}</td>
                    <td style={{ padding: "9px 20px" }}>
                      <span style={{
                        padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                        color:       h.status === "completed" ? T.green  : h.status === "failed" ? T.red  : T.orange,
                        background:  h.status === "completed" ? T.greenBg : h.status === "failed" ? T.redBg : T.orangeBg,
                        border: "1px solid " + (h.status === "completed" ? T.green : h.status === "failed" ? T.red : T.orange) + "44",
                      }}>{h.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
